import { useCallback, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import type { InitiativeWriteInput } from '@complan/contracts';
import { usePageChrome } from '@/components/layout/page-chrome';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { ApiRequestError } from '@/shared/api/api-error';
import { useInitiativeDetail } from '@/features/initiative/detail-data';
import { mapDetailToWriteInput } from '@/features/initiative/detail-to-write';
import { IntakeWizard } from './IntakeWizard';
import { extractVersionConflict, useIntakeOptions, useUpdateInitiative } from './intake-data';
import { fromServerFinding, type SummaryFinding } from './validation-model';

/**
 * The Initiative_Intake_Form wizard mounted for an **existing** record
 * (`/initiative/:id/edit`), the Detail_Panel footer's Edit action.
 *
 * A full-page route, like `/intake`, rather than a nested drawer route: editing
 * is the same nine-step wizard a create runs, not a Detail_Panel view, so it
 * carries none of the calendar's filter/orientation/sort state the drawer routes
 * preserve.
 *
 * Three reads feed the wizard before it can render: the record itself (for the
 * default values and the loaded `version` the update guards against), and the
 * controlled-value option sets — loaded with the record's *currently held* ids
 * as `referenced`, so an option this record already selected that has since been
 * deactivated still renders, marked inactive, rather than silently vanishing from
 * the field (R5.9).
 */
export function IntakeEditPage(): React.JSX.Element {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const params = useParams();
  const initiativeId = params['id'] ?? '';

  const detailQuery = useInitiativeDetail(initiativeId);
  const detail = detailQuery.data;

  usePageChrome({
    title: t('intake.editTitle'),
    subtitle: detail?.title ?? t('intake.subtitle'),
  });

  const referenced = useMemo(
    () =>
      detail === undefined
        ? {}
        : {
            owners: [detail.ownerUserId, ...(detail.backupOwnerUserId ? [detail.backupOwnerUserId] : [])],
            platforms: detail.platforms.map((platform) => platform.platformId),
            channels: [...detail.channelIds],
            audiences: [...detail.audienceIds],
            customerGroups: [...detail.customerGroupIds],
            categories: [...detail.categoryIds],
            initiativeTypes: [...detail.initiativeTypeIds],
            cscAttributes: [...detail.cscAttributeIds],
            priorities: [detail.priorityId],
            milestoneTypes: detail.milestones.map((milestone) => milestone.milestoneTypeId),
            fieldForceActivityTypes: detail.fieldForceScope.map((scope) => scope.activityTypeId),
          },
    [detail],
  );
  const { options, isLoading: optionsLoading, isError: optionsError } = useIntakeOptions(referenced);

  const update = useUpdateInitiative(initiativeId, detail?.version ?? 0);
  const [submitError, setSubmitError] = useState<unknown>(null);

  // Same adapter and same "findings vs. plain alert" split as the create route
  // (IntakePage.tsx): a validation_failed rejection's findings join the summary,
  // any other failure surfaces as an alert with nothing to put in the summary.
  const submitFindings = useMemo<readonly SummaryFinding[]>(() => {
    if (!(submitError instanceof ApiRequestError) || submitError.body.code !== 'validation_failed') {
      return [];
    }
    return submitError.body.findings.map(fromServerFinding);
  }, [submitError]);

  const versionConflict = extractVersionConflict(submitError);

  const showErrorAlert =
    submitError !== null &&
    versionConflict === null &&
    !(submitError instanceof ApiRequestError && submitError.body.code === 'validation_failed');

  const goToDetail = useCallback((): void => {
    navigate(`/calendar/${String(new Date().getFullYear())}/initiative/${initiativeId}`);
  }, [navigate, initiativeId]);

  const handleSubmit = useCallback(
    (payload: InitiativeWriteInput): void => {
      setSubmitError(null);
      update.mutate(payload, {
        onSuccess: goToDetail,
        onError: (error: unknown) => {
          setSubmitError(error);
        },
      });
    },
    [update, goToDetail],
  );

  if (detailQuery.isLoading) {
    return <p className="text-muted-foreground p-4 text-[12.3px]">{t('common.loading')}</p>;
  }
  if (detail === undefined) {
    return (
      <div className="p-4">
        <Alert variant="destructive">
          <AlertTitle>{t('detail.notFound')}</AlertTitle>
        </Alert>
      </div>
    );
  }
  if (optionsError) {
    return (
      <div className="p-4">
        <Alert variant="destructive">
          <AlertTitle>{t('intake.optionsError.title')}</AlertTitle>
          <AlertDescription>{t('intake.optionsError.description')}</AlertDescription>
        </Alert>
      </div>
    );
  }
  if (optionsLoading) {
    return <p className="text-muted-foreground p-4 text-[12.3px]">{t('common.loading')}</p>;
  }

  return (
    <div className="min-w-0">
      {showErrorAlert ? (
        <Alert variant="destructive" className="mb-3">
          <AlertTitle>{t('intake.submitError.title')}</AlertTitle>
          <AlertDescription>{t('intake.submitError.description')}</AlertDescription>
        </Alert>
      ) : null}
      <IntakeWizard
        options={options}
        defaultValues={mapDetailToWriteInput(detail)}
        submitFindings={submitFindings}
        initiativeId={initiativeId}
        loadedVersion={detail.version}
        versionConflict={versionConflict}
        onDismissConflict={() => setSubmitError(null)}
        onSubmit={handleSubmit}
        onRequestLeave={goToDetail}
      />
    </div>
  );
}
